import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext();

const STORAGE_KEY = 'theme';

// Dark mode UI is temporarily disabled. Keep theme plumbing intact so this can
// be flipped back on without rewiring Layout / CSS.
export const DARK_MODE_ENABLED = false;

const getPreferredTheme = () => {
    if (!DARK_MODE_ENABLED) return 'light';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    // Default to light for all first-time / unset users — never follow OS dark preference
    return 'light';
};

const applyTheme = (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(getPreferredTheme);

    useEffect(() => {
        const resolved = DARK_MODE_ENABLED ? theme : 'light';
        applyTheme(resolved);
        localStorage.setItem(STORAGE_KEY, resolved);
    }, [theme]);

    const setTheme = useCallback((next) => {
        if (!DARK_MODE_ENABLED) {
            setThemeState('light');
            return;
        }
        setThemeState(next);
    }, []);

    const toggleTheme = useCallback(() => {
        if (!DARK_MODE_ENABLED) return;
        setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
    }, []);

    const resolvedTheme = DARK_MODE_ENABLED ? theme : 'light';
    const isDark = resolvedTheme === 'dark';

    return (
        <ThemeContext.Provider
            value={{
                theme: resolvedTheme,
                isDark,
                toggleTheme,
                setTheme,
                darkModeEnabled: DARK_MODE_ENABLED,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
